import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { isMicrosoftChildSource } from '@/features/SourceCreate/SourceCreate.utils';
import type { LimaSourceAggregatedStatusModel } from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';

import { DEVICE_TYPE_LIMA } from '../DevicesTable.deviceTypes';
import type {
  DetailedSourceModel,
  DeviceModel,
  DeviceTreeDataBuilderOptions,
  DeviceTreeNode
} from '../DevicesTable.types';
import { STATUS_PRIORITY } from './LimaSourceAggregatedStatus.types';

/**
 * Calculate highest status code from Microsoft data providers
 * @param device - Device model containing aggregated status data
 * @param deviceUid - UID of the device to filter providers
 * @returns Highest status code among Microsoft providers, or null if none found
 */
const calculateMicrosoftHighestStatusCode = (
  device: DeviceModel,
  deviceUid: string
): number | null => {
  if (!device.lima_source_aggregated_status_display) return null;

  const msProviders = device.lima_source_aggregated_status_display.filter(
    (s: LimaSourceAggregatedStatusModel) =>
      s.device_uid === deviceUid && isMicrosoftChildSource(s.source_type_id)
  );

  if (msProviders.length === 0) return null;

  // Find highest severity level present
  const highestSeverity = Math.max(
    ...msProviders.map((s) =>
      s.highest_status !== undefined
        ? (STATUS_PRIORITY[s.highest_status] ?? 0)
        : 0
    )
  );

  // From the highest severity, collect valid status codes
  const codes = msProviders
    .filter(
      (s) =>
        s.highest_status !== undefined &&
        STATUS_PRIORITY[s.highest_status] === highestSeverity &&
        typeof s.highest_status_code === 'number'
    )
    .map((s) => s.highest_status_code as number);

  return codes.length > 0 ? Math.max(...codes) : null;
};

/**
 * Calculate total Microsoft source count from group_* fields
 * @param aggStatus - Aggregated status object containing group counts
 * @returns Total count from all group_* fields
 */
const calculateMicrosoftSourceCount = (
  aggStatus: LimaSourceAggregatedStatusModel
): number => {
  return Object.entries(aggStatus)
    .filter(([key]) => key.startsWith('group_'))
    .reduce(
      (sum, [, value]) =>
        sum + (typeof value === 'number' ? value : Number(value) || 0),
      0
    );
};

/**
 * Build device node from device model
 * @param device - Device model to convert
 * @param selectedOrganizationId - Currently selected organization ID
 * @returns Device tree node
 */
const buildDeviceNode = (
  device: DeviceModel,
  selectedOrganizationId: number
): DeviceTreeNode => {
  const isLimaDevice = device.device_type_id === DEVICE_TYPE_LIMA;

  const hasChildren =
    isLimaDevice &&
    device.lima_source_aggregated_status_display &&
    device.lima_source_aggregated_status_display.length > 0;

  return {
    id: device.device_uid,
    path: [device.device_uid],
    type: 'device',
    name: device.display_name || device.host_name,
    deviceType: device.device_type?.type || '',
    ipAddress: device.ip_address,
    status: device.status,
    statusLabel: device.status_label,
    statusUpdatedAt: device.status_updated_at_display,
    lastSeen: device.last_seen_display,
    organization:
      selectedOrganizationId === 0 ? device.organization?.label : undefined,
    sourceCount: device.source_count,
    sourceStatus: device.source_status,
    actionType: device.actionType,
    isSyncInProgress: device.isSyncInProgress,
    deviceData: device,
    hasChildren,
    level: 0
  };
};

/**
 * Build flat source nodes for non-LIMA devices
 * @param device - Device model containing sources
 * @param selectedOrganizationId - Currently selected organization ID
 * @returns Array of source tree nodes
 */
const buildFlatSourceNodes = (
  device: DeviceModel,
  selectedOrganizationId: number
): DeviceTreeNode[] => {
  if (device.device_type_id === 1 || !device.sources) return [];

  return device.sources.map((source: DetailedSourceModel) => ({
    id: source.source_uid,
    path: [device.device_uid, source.source_uid],
    type: 'source',
    name: source.source_name,
    ipAddress: source.ip_address,
    status: source.status ?? undefined,
    statusLabel: source?.status_label || '',
    statusUpdatedAt: source?.status_updated_at_display || '',
    lastSeen: source?.last_seen_display || '',
    organization:
      selectedOrganizationId === 0 ? source.organization?.label : undefined,
    sourceData: source,
    deviceData: device,
    hasChildren: false,
    level: 1
  }));
};

/**
 * Calculate Microsoft aggregate information
 * @param device - Device model
 * @param aggStatus - Aggregated status record
 * @param isMicrosoftSource - Whether this is a Microsoft source
 * @returns Object with Microsoft source count and highest status code
 */
const calculateMicrosoftAggregates = (
  device: DeviceModel,
  aggStatus: LimaSourceAggregatedStatusModel,
  isMicrosoftSource: boolean
): {
  msSourceCount: number;
  msHighestStatusCode: number | null;
} => {
  if (!isMicrosoftSource) {
    return {
      msSourceCount: 0,
      msHighestStatusCode: null
    };
  }

  const allStatuses = device.lima_source_aggregated_status || [];
  const displayStatuses = device.lima_source_aggregated_status_display || [];

  // Check if Microsoft child providers exist
  const hasMsProviders = displayStatuses.some(
    (s) =>
      s.device_uid === device.device_uid &&
      isMicrosoftChildSource(s.source_type_id)
  );

  if (!hasMsProviders) {
    return {
      msSourceCount: 0,
      msHighestStatusCode: null
    };
  }

  // Get Microsoft providers (child sources)
  const msProviders = allStatuses.filter(
    (s) =>
      s.device_uid === device.device_uid &&
      isMicrosoftChildSource(s.source_type_id)
  );

  // Calculate highest status
  const msHighestStatusCode = msProviders.length
    ? Math.max(
        ...msProviders
          .map((p) => p.status_code)
          .filter((code): code is number => code != null)
      )
    : null;

  // Calculate total source count from group_* fields
  let msSourceCount = 0;

  if (aggStatus) {
    msSourceCount = Object.keys(aggStatus)
      .filter((key) => key.startsWith('group_'))
      .reduce((sum, key) => {
        const value = aggStatus[key as keyof typeof aggStatus];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
  }

  return {
    msSourceCount,
    msHighestStatusCode
  };
};

/**
 * Build Microsoft provider nodes under a Microsoft source
 * @param device - Device model
 * @param sourcePath - Path array of the parent source
 * @returns Array of provider tree nodes
 */
const buildMicrosoftProviderNodes = (
  device: DeviceModel,
  sourcePath: string[]
): DeviceTreeNode[] => {
  const providers =
    device.lima_source_aggregated_status_display?.filter(
      (s) =>
        s.device_uid === device.device_uid &&
        isMicrosoftChildSource(s.source_type_id)
    ) || [];

  return providers.map((provider) => {
    const providerId = `${device.device_uid}-${provider.source_type_id}`;
    const providerPath = [...sourcePath, providerId];

    return {
      id: providerId,
      path: providerPath,
      type: 'provider',
      name: provider.name || '',
      status: provider.status ?? '',
      statusLabel: provider.status_label ?? '',
      lastSeen: provider.last_seen_display ?? undefined,
      sourceCount: calculateMicrosoftSourceCount(provider),
      highestStatusCode:
        calculateMicrosoftHighestStatusCode(device, device.device_uid) ??
        undefined,
      providerData: provider,
      deviceData: device,
      hasChildren: false,
      level: 2
    };
  });
};

/**
 * Build LIMA source nodes with optional Microsoft provider children
 * @param device - Device model
 * @param includeMicrosoftProviders - Whether to include Microsoft child providers
 * @returns Array of source and provider tree nodes
 */
const buildLimaSourceNodes = (
  device: DeviceModel,
  includeMicrosoftProviders: boolean
): DeviceTreeNode[] => {
  if (device.device_type_id !== 1) return [];

  const devicePath = [device.device_uid];

  const filteredStatuses =
    device.lima_source_aggregated_status_display?.filter(
      (s) =>
        s.device_uid === device.device_uid &&
        !isMicrosoftChildSource(s.source_type_id)
    ) || [];

  return filteredStatuses.flatMap((aggStatus) => {
    const sourceId = `${device.device_uid}-${aggStatus.source_type_id}`;
    const sourcePath = [...devicePath, sourceId];

    const isMicrosoftSource =
      aggStatus.source_type_id === SOURCE_TYPE.MICROSOFT;

    const hasMsProviders =
      isMicrosoftSource &&
      device.lima_source_aggregated_status_display?.some(
        (s) =>
          s.device_uid === device.device_uid &&
          isMicrosoftChildSource(s.source_type_id)
      );

    const { msSourceCount, msHighestStatusCode } = calculateMicrosoftAggregates(
      device,
      aggStatus,
      isMicrosoftSource
    );

    const sourceNode: DeviceTreeNode = {
      id: sourceId,
      path: sourcePath,
      type: 'source',
      source_type_id: aggStatus.source_type_id,
      name: aggStatus.name || '',
      status: aggStatus.status,
      statusLabel: aggStatus?.status_label || '',
      lastSeen: aggStatus?.last_seen_display || '',
      sourceCount: isMicrosoftSource
        ? msSourceCount
        : (aggStatus?.source_count ?? 0),
      highestStatusCode: msHighestStatusCode ?? undefined,
      providerData: aggStatus,
      deviceData: device,
      hasChildren: hasMsProviders && includeMicrosoftProviders,
      level: 1
    };

    const providerNodes =
      hasMsProviders && includeMicrosoftProviders
        ? buildMicrosoftProviderNodes(device, sourcePath)
        : [];

    return [sourceNode, ...providerNodes];
  });
};

/**
 * Transforms hierarchical device data into flat tree rows for AG Grid
 *
 * Builds a flattened array of tree nodes from device models, including:
 * - Device nodes (level 0)
 * - Source nodes (level 1) - for both LIMA aggregated sources and flat device sources
 * - Provider nodes (level 2) - for Microsoft child providers
 *
 * @param devices - Array of device models with nested sources and providers
 * @param options - Configuration options for tree building
 * @returns Flattened array of tree nodes suitable for AG Grid Tree Data
 *
 * @example
 * ```tsx
 * const treeData = buildDeviceTreeRows(devices, {
 *   selectedOrganizationId: 1,
 *   includeMicrosoftProviders: true
 * });
 * ```
 */
export const buildDeviceTreeRows = (
  devices: DeviceModel[],
  options: DeviceTreeDataBuilderOptions
): DeviceTreeNode[] => {
  const { selectedOrganizationId, includeMicrosoftProviders = true } = options;

  return devices.flatMap((device) => {
    const deviceNode = buildDeviceNode(device, selectedOrganizationId);
    const flatSources = buildFlatSourceNodes(device, selectedOrganizationId);
    const limaSources = buildLimaSourceNodes(device, includeMicrosoftProviders);

    return [deviceNode, ...flatSources, ...limaSources];
  });
};

/**
 * Helper to determine if a node should show expand/collapse icon
 * @param node - Device tree node to check
 * @returns True if node has children and should show expand icon
 */
export const shouldShowExpandIcon = (node: DeviceTreeNode): boolean => {
  return node.hasChildren === true;
};
